import { IAPIResponseError, IError } from '@checkout/errors';
import { ISelectionResponse } from '@checkout/items';
import {
  IChangePaymentMethodSuccessPayload,
  IFetchWidgetSuccessPayload,
  IPaymentResultSuccessPayload,
  IUpdatePaymentFieldsPayload } from '@checkout/payloadTypes';
import { IPaymentResponse } from '@checkout/payments';
import { IAddress, ICartFieldsData, IFieldsData, IPaymentRequest, RootState } from '@checkout/state';
import { AxiosResponse } from 'axios';
import { Epic, ofType } from 'redux-observable';
import { concat as concat$ } from 'rxjs/observable/concat';
import { fromPromise as fromPromise$ } from 'rxjs/observable/fromPromise';
import { of as of$ } from 'rxjs/observable/of';
import { catchError, flatMap, mergeMap, tap } from 'rxjs/operators';
import { checkoutApi } from 'ys-analytics';
import { RootAction } from '../actions/actionTypes.d';
import Address from '../modules/Address';
import convertMessages from '../utils/convertErrors';
import { isKlarnaCheckout } from '../utils/paymentMethods';
import { deleteCookie, getCookie } from '../utils/persistence';
import { ingridResume, ingridSuspend, kcoResume, kcoSuspend } from '../utils/widgets';
import ActionTypes from './../actions/actionTypes';
import {
  ChangePaymentMethodRequestAction,
  CheckIngridDeliveryOptionRequestAction,
  PaymentFieldsRequestAction,
  UpdateNewsletterFieldRequestAction } from './../actions/actionTypes.d';
import {
  checkIngridDeliveryOptionSuccess,
  checkoutSuccess,
  fetchKCOWidgetCancel,
  fetchKCOWidgetRequest,
  fetchKCOWidgetSuccess,
  fetchWidgetSuccess,
  paymentMethodChangeSuccess,
  paymentResultSuccess,
  responseError,
  setErrors,
  updateNewsletterFieldSuccess,
  updatePaymentFieldsSuccess
} from './../reducers/checkout';
import { getQueryParametersAsObject, rest } from './../utils/apiUtils';
import { logger } from './../utils/logger';

const { eePurchase, eeCheckoutSteps, fbPurchase } = checkoutApi;

export const fetchKCOWidgetEpic: Epic<RootAction, RootAction, RootState> =
  (action$, state$) => action$.pipe(
    ofType(ActionTypes.FETCH_KCO_WIDGET_REQUEST),
    mergeMap((action) => {
      const { country, state, address1, address2, zipCode, company, firstName, lastName, city } = state$.value.checkout.fieldsData.shippingAddress;
      // !state$.value.checkout.widgets[state$.value.checkout.selection.paymentMethod]
      const email = state$.value.checkout.fieldsData.contactDetails.email;
      const { failure, success } = state$.value.checkout.pathnames;

      kcoSuspend();

      // type Request = Partial<IPaymentRequest> & { address: Partial<IAddress>};

      type PaymentRequest = {
        address: Partial<IAddress> &
          Partial<Pick<IAddress, 'country' | 'zipCode' | 'email' | 'address1' | 'address2' | 'company' | 'firstName' | 'lastName' | 'city'>>;
      } & Pick<IPaymentRequest, 'paymentReturnPage' | 'paymentFailedPage'>;

      const paymentRequest: PaymentRequest = {
        address: {
          email: email !== '' ? email : undefined,
          country: country !== '' ? country : undefined,
          state: state !== '' ? state : undefined,
          address1: address1 !== '' ? address1 : undefined,
          address2: address2 !== '' ? address2 : undefined,
          zipCode: zipCode !== '' ? zipCode : undefined,
          company: company !== '' ? company : undefined,
          firstName: firstName !== '' ? firstName : undefined,
          lastName: lastName !== '' ? lastName : undefined,
          city: city !== '' ? city : undefined,
        },
        paymentReturnPage: `${window.location.origin}${success}`,
        paymentFailedPage: `${window.location.origin}${failure}`
      };

      if (state$.value.checkout.selection && state$.value.checkout.selection.items && state$.value.checkout.selection.items.length > 0) {
        return fromPromise$(rest.httpPost('/payment', paymentRequest))
          .pipe(
            flatMap((response: AxiosResponse<IFetchWidgetSuccessPayload>) => {
              const { paymentMethod: selectedPaymentMethod } = state$.value.checkout.selection;
              kcoResume();

              return of$(fetchKCOWidgetSuccess(Object.assign({}, response.data, { selectedPaymentMethod })));
            }),
            catchError((error: IError) => {
              logger(error);

              if (error.response && error.response.data && error.response.data.selection) {
                const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData);
                const responseApiErrorPayload: IAPIResponseError = {
                  errors: convertedMessages,
                  selection: error.response.data.selection,
                  unavailable: error.response.data.unavailable
                };
                // Try to get KCO widget again when error with unavailable occures
                if (error.response.data.unavailable && error.response.data.unavailable.length > 0) {
                  return concat$(
                    of$(responseError(responseApiErrorPayload)),
                    of$(fetchKCOWidgetRequest())
                  );
                } else {
                  return of$(responseError(responseApiErrorPayload));
                }
              }
              return of$(setErrors(error));
            })
          );
      } else {
        return of$(fetchKCOWidgetCancel());
      }
    })
  );

export const paymentResultEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    ofType(ActionTypes.PAYMENT_RESULT_REQUEST),
    mergeMap((action) => {
      const paymentMethodFields = window.SilkPaymentPayload || getQueryParametersAsObject(location.search);

      return fromPromise$(rest.httpPost('/payment-result', { paymentMethodFields }))
        .pipe(
          flatMap((response: AxiosResponse<IPaymentResultSuccessPayload>) => {
            const { order } = response.data;
            if (isKlarnaCheckout(order.paymentMethod)) {
              try {
                eeCheckoutSteps.klarnaFilled(order.currency);
              } catch (err) {
                // tslint:disable-next-line:no-console
                console.error('Enhanced Ecommerce error: ', err);
              }
              try {
                eeCheckoutSteps.placedOrder(order.currency);
              } catch (err) {
                // tslint:disable-next-line:no-console
                console.error('Enhanced Ecommerce error: ', err);
              }
            }
            try {
              eePurchase(order);
            } catch (err) {
              // tslint:disable-next-line:no-console
              console.error('Enhanced Ecommerce error: ', err);
            }
            try {
              fbPurchase(order);
            } catch (err) {
              // tslint:disable-next-line:no-console
              console.error('Enhanced Ecommerce error: ', err);
            }
            // Reset Centra session
            // when user is logged in do not delete session token because user needs to be still logged in
            if (!response.data.loggedIn) {
              deleteCookie('token', '/');
            }
            return of$(paymentResultSuccess(response.data));
          }),
          catchError((error: IError) => {
            logger(error);
            // Redirect to failure page
            window.location.href = state$.value.checkout.pathnames.failure + location.search;
            return of$(setErrors(error));
          })
        );
    })
  );

export const paymentResultFailureEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    ofType(ActionTypes.PAYMENT_RESULT_FAILURE),
    mergeMap((action) => {
      const paymentMethodFields = window.SilkPaymentPayload || getQueryParametersAsObject(location.search);

      return fromPromise$(rest.httpPost('/payment-result', { paymentMethodFields }))
        .pipe(
          flatMap(() => of$()),
          catchError((error: IError) => {
            logger(error);
            if (error.response && error.response.data) {
              const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData);
              const responseApiErrorPayload: IAPIResponseError = { errors: convertedMessages };
              return of$(responseError(responseApiErrorPayload));
            }
            return of$(setErrors(error));
          })
        );
    })
  );

export const checkIngridDeliveryOptionEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    ofType(ActionTypes.INGRID_DELIVERY_OPTION_CHECK_REQUEST),
    tap(() => { ingridSuspend(); }),
    mergeMap((action: CheckIngridDeliveryOptionRequestAction) => {
      return fromPromise$(rest.httpPut('/payment-fields', action.payload))
        .pipe(
          flatMap((response: AxiosResponse<IUpdatePaymentFieldsPayload>) => {
            ingridResume();
            return of$(checkIngridDeliveryOptionSuccess(response.data));
          }),
          catchError((error: IError) => {
            logger(error);
            ingridResume();
            return of$(setErrors(error));
          })
        );
    })
  );

export const paymentFieldsEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    ofType(ActionTypes.UPDATE_PAYMENT_FIELDS_REQUEST),
    tap((action: PaymentFieldsRequestAction) => {
      if (action.payload.suspendIgnore !== undefined && !action.payload.suspendIgnore) {
        ingridSuspend();
      }
      kcoSuspend();
    }),
    mergeMap((action: PaymentFieldsRequestAction) => {
      const { suspendIgnore: _, ...restPayload } = action.payload;
      return fromPromise$(rest.httpPut('/payment-fields', restPayload))
        .pipe(
          flatMap((response: AxiosResponse<IUpdatePaymentFieldsPayload>) => {
            if (action.payload.suspendIgnore !== undefined && !action.payload.suspendIgnore) {
              ingridResume();
            }
            kcoResume();
            return of$(updatePaymentFieldsSuccess(response.data));
          }),
          catchError((error: IError) => {
            logger(error);
            kcoResume();
            ingridResume();
            return of$(setErrors(error));
          })
        );
    })
  );

export const newsletterFieldEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    ofType(ActionTypes.UPDATE_NEWSLETTER_FIELD_REQUEST),
    mergeMap((action: UpdateNewsletterFieldRequestAction) => {
      return fromPromise$(rest.httpPut('/payment-fields', action.payload))
        .pipe(
          flatMap((response: AxiosResponse<ISelectionResponse>) => {
            return of$(updateNewsletterFieldSuccess({ newsletter: action.payload.address.newsletter }));
          }),
          catchError((error: IError) => {
            logger(error);
            return of$(setErrors(error));
          })
        );
    })
  );

export const changePaymentMethodEpic: Epic<RootAction, RootAction, RootState> =
  (action$, state$) => action$.pipe(
    ofType(ActionTypes.CHANGE_PAYMENT_METHOD_REQUEST),
    mergeMap((action: ChangePaymentMethodRequestAction) => {
      const { paymentMethod } = action.payload;

      return fromPromise$(rest.httpPut(`/payment-methods/${paymentMethod}`))
        .pipe(
          flatMap((response: AxiosResponse<IChangePaymentMethodSuccessPayload>) => {
            const { currency, paymentMethod: payloadPaymentMethod } = response.data.selection;
            try {
              eeCheckoutSteps.paymentChange(payloadPaymentMethod, currency);
            } catch (err) {
              // tslint:disable-next-line:no-console
              console.error('Enhanced Ecommerce error: ', err);
            }
            if (isKlarnaCheckout(paymentMethod)) {
              return concat$(
                // fetchKCOWidgetRequest(),
                of$(paymentMethodChangeSuccess(response.data)),
              );
            }
            return of$(paymentMethodChangeSuccess(response.data));
          }),
          catchError((error: IError) => {
            logger(error);
            return of$(setErrors(error));
          })
        );
    })
  );

export const handleCheckoutEpic: Epic<RootAction, RootAction, RootState> =
  (action$, state$) => action$.pipe(
    ofType(ActionTypes.CHECKOUT_REQUEST),
    mergeMap((action) => {
      const fieldsData = state$.value.checkout.fieldsData;
      const cartFieldsData = state$.value.checkout.cartFieldsData;
      const { failure, success } = state$.value.checkout.pathnames;
      const paymentReturnPage = `${window.location.origin}${success}`;
      const paymentFailedPage = `${window.location.origin}${failure}`;
      const { currency, paymentMethod } = state$.value.checkout.selection;

      const checkoutRequest: IPaymentRequest =
        prepareCheckoutRequest(paymentReturnPage, paymentFailedPage, fieldsData, cartFieldsData);

      if (!isKlarnaCheckout(paymentMethod)) {
        try {
          eeCheckoutSteps.placedOrder(currency);
        } catch (err) {
          // tslint:disable-next-line:no-console
          console.error('Enhanced Ecommerce error: ', err);
        }
      }

      return fromPromise$(rest.httpPost('/payment', checkoutRequest))
        .pipe(
          flatMap((response: AxiosResponse<IPaymentResponse>) => {
            const { action: checkoutAction, url } = response.data;

            switch (checkoutAction) {
              case 'redirect':
                if (url) {
                  window.location.href = url; // Redirect to the payment provider website
                }
                break;
              case 'form':
                const widgetPayload = Object.assign({}, response.data, { selectedPaymentMethod: paymentMethod });
                return of$(fetchWidgetSuccess(widgetPayload));
              case 'success':
                window.location.pathname = success || '/receipt'; // Go straight to the receipt page
                break;
              default:
                // tslint:disable-next-line:no-console
                console.log('checkoutAction:', checkoutAction);
            }
            return of$(checkoutSuccess());
          }),
          catchError((error: IError) => {
            logger(error);
            if (error.response && error.response.data && error.response.data.selection) {
              const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData);
              const responseApiErrorPayload: IAPIResponseError = {
                errors: convertedMessages,
                selection: error.response.data.selection,
                unavailable: error.response.data.unavailable
              };
              return of$(responseError(responseApiErrorPayload));
            } else if (error.response && error.response.data) {
              const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData);
              return of$(setErrors(convertedMessages));
            }
            return of$(setErrors(error));
          })
        );
    })
  );

function prepareCheckoutRequest(
  paymentReturnPage: string,
  paymentFailedPage: string,
  fieldsData: IFieldsData,
  cartFieldsData: ICartFieldsData): IPaymentRequest {

  const paymentAddress: IAddress = Address.create(
    {
      email: fieldsData.contactDetails.email,
      phoneNumber: fieldsData.contactDetails.phoneNumber,
      firstName: fieldsData.shippingAddress.firstName,
      lastName: fieldsData.shippingAddress.lastName,
      address1: fieldsData.shippingAddress.address1,
      address2: fieldsData.shippingAddress.address2,
      houseNumber: fieldsData.shippingAddress.houseNumber,
      zipCode: fieldsData.shippingAddress.zipCode,
      company: fieldsData.shippingAddress.company,
      city: fieldsData.shippingAddress.city,
      state: fieldsData.shippingAddress.state,
      country: fieldsData.shippingAddress.country,
      identityNumber: fieldsData.shippingAddress.identityNumber,
    },
  );

  const { identityNumber, ...shippingAddress } = paymentAddress;

  const billingAddress: IAddress = Address.create(
    {
      email: fieldsData.contactDetails.email,
      phoneNumber: fieldsData.contactDetails.phoneNumber,
      firstName: fieldsData.billingAddress.firstName,
      lastName: fieldsData.billingAddress.lastName,
      address1: fieldsData.billingAddress.address1,
      address2: fieldsData.billingAddress.address2,
      houseNumber: fieldsData.billingAddress.houseNumber,
      zipCode: fieldsData.billingAddress.zipCode,
      company: fieldsData.billingAddress.company,
      city: fieldsData.billingAddress.city,
      state: fieldsData.billingAddress.state,
      country: fieldsData.billingAddress.country,
      newsletter: cartFieldsData.newsletter,
      identityNumber: fieldsData.shippingAddress.identityNumber,
    },
  );

  const checkoutRequest: IPaymentRequest = {
    paymentReturnPage,
    paymentFailedPage,
    address: fieldsData.billingAddress.billingSameAsShipping ?
      {
        ...paymentAddress,
        newsletter: cartFieldsData.newsletter
      } :
      billingAddress,
    shippingAddress,
    termsAndConditions: true,
    additionalNotes: cartFieldsData.additionalNotes.text !== '' ? cartFieldsData.additionalNotes.text : undefined
  };

  /*
    Symfony frontend set a cookie when someone enters website coming from tradedoubler (or any other affiliate)
    Get the content of that "affiliateId" cookie, and send it as "affiliate" to the API to POST /payment endpoint
  */
  const affiliate = getCookie('affiliateId');
  if (affiliate || window.affiliateId) {
    checkoutRequest.affiliate = affiliate || window.affiliateId;
  }

  return checkoutRequest;
}
